import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useClientApi, useOrderCreateMultiApi } from '@hero/api-hooks';
import {
  buildCreateMultiOrderPayload,
  getAutoDateRange,
  selectIsReadyToPublish,
  selectTotalVehicles,
  useIsLogistiConnected,
  usePublishOrderStore,
  type PublishSessionEntry,
} from '@hero/hooks-shared';
import { useNav } from '@hero/web-hooks';
import {
  useApplyClientOrderDefaultsManager,
  usePublishOrderPricingManager,
} from '@hero/web-hooks';
import { PickupDateDrawer } from '../../PickupDate/PickupDateDrawer';
import { rolesToLegacyCustomerType } from './customerType';
// TODO(shipping-revisit): the "who will ship this order" picker is hidden
// for now. The store still defaults `allocationStrategy` to `'auto'`, so
// the publish payload behaves as if Auto was chosen. We'll bring this UI
// back once the new allocation flow is finalized.
// import { CarrierSection } from './CarrierSection';
import { ClientSection } from './ClientSection';
import { LoadDetailsFormDrawer } from './LoadDetailsFormDrawer';
import { PublishGrid } from './PublishGrid';
import { PublishSidePanel } from './PublishSidePanel';
import { RouteSection } from './RouteSection';

export interface PublishOrderPageProps {
  /**
   * Called when the user clicks "Done" in the side panel. The page stays
   * mounted for repeated entries; `onClose` is only fired when the carrier
   * explicitly chooses to leave.
   */
  onClose?: () => void;
  /**
   * Overrides the default toast on validation failure (e.g. for embedding
   * in a flow that wants to surface errors differently).
   */
  onValidationError?: (message: string) => void;
}

/**
 * Single-page publish-order experience used by carrier-dashboard (and
 * eventually customer-dashboard once we migrate it away from Redux).
 *
 * Repeated-entry model: the page never navigates away on success.
 * Instead we (a) push a snapshot of the publish into `sessionOrders`,
 * (b) soft-reset the form (sticky lane fields stay, per-order fields
 * clear), (c) fire a non-blocking toast. The side panel renders the
 * running "This session" list so the carrier sees their own progress.
 */
export function PublishOrderPage({ onClose, onValidationError }: PublishOrderPageProps) {
  const { t } = useTranslation('m-order');
  const { navigate, getParams } = useNav();

  const isReady = usePublishOrderStore(selectIsReadyToPublish);
  // Kept out of `selectIsReadyToPublish` deliberately: that selector is a pure
  // read of the draft, and whether goods value is mandatory depends on the
  // Org's Logisti install, which is server state.
  const isGoodsValueRequired = useIsLogistiConnected();
  const itemCost = usePublishOrderStore(s => s.load.itemCost);
  const reset = usePublishOrderStore(s => s.reset);
  const pushSessionEntry = usePublishOrderStore(s => s.pushSessionEntry);
  const resetForNext = usePublishOrderStore(s => s.resetForNext);
  const setPickupDate = usePublishOrderStore(s => s.setPickupDate);
  const pickupDate = usePublishOrderStore(s => s.pickupDate);
  const setCustomer = usePublishOrderStore(s => s.setCustomer);

  // The load drawer + pickup modal live at the page level so the side
  // panel rows can open them directly — keeps the form column tighter.
  const [isLoadOpen, setLoadOpen] = useState(false);
  const [isPickupOpen, setPickupOpen] = useState(false);

  // Phase 2 (relations-v2) — auto-apply order defaults from
  // `Org.settings` (and, for business creators, the picked
  // `Client.settings`) into the publish-store load draft. Sticky
  // overrides for any field the user has already touched are
  // preserved (see `applyLoadDefaults` / `loadOverrides`). Mounted
  // here at the page level so the effect runs once per session and
  // re-runs whenever the picked client changes.
  useApplyClientOrderDefaultsManager();

  // Drives the per-destination pricing preview the route cards and
  // side panel breakdown read from. Re-fires when the lane / vehicle
  // / client changes; debounced so destination-picking keystrokes
  // don't hammer the resolver.
  usePublishOrderPricingManager();

  // CRM hand-off — when the user came from the Orders tab "New
  // order" CTA, the URL carries `?clientId=<hex>`. Fetch that client
  // explicitly and prime the publish store before the user can
  // touch the form, so the rest of the page (pricing, side panel,
  // load defaults) reacts to it as if the user had picked the
  // client manually.
  const urlClientId = getParams('new_order').clientId;
  const { data: prefilledClient } = useClientApi(urlClientId);
  const hasHydratedClient = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!urlClientId || !prefilledClient) return;
    if (hasHydratedClient.current === urlClientId) return;
    hasHydratedClient.current = urlClientId;
    setCustomer({
      customerId: String(prefilledClient._id),
      name: prefilledClient.name,
      type: rolesToLegacyCustomerType(prefilledClient.roles),
      phone: prefilledClient.primaryContact?.phone ?? undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlClientId, prefilledClient]);

  const createOrder = useOrderCreateMultiApi({
    onSuccess: orders => {
      const snapshot = usePublishOrderStore.getState();
      const orderIds = (orders ?? []).map(o => String(o._id));
      const entry: PublishSessionEntry = {
        id:
          typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        orderIds,
        createdAt: Date.now(),
        receiverName: snapshot.isInternalShipment ? undefined : snapshot.customer?.name,
        isInternalShipment: snapshot.isInternalShipment,
        vehicleCount: selectTotalVehicles(snapshot),
        routeCount: snapshot.destinations.length,
        pickupFromIso: snapshot.pickupDate?.from,
      };
      pushSessionEntry(entry);
      resetForNext();
      toast.success(
        t('wizard.publish.created_toast', { defaultValue: 'Order created' }),
      );
      // After a successful publish, return the carrier to the
      // dashboard home rather than the order drawer. The previous
      // behaviour (`navigate('orders', { id: firstOrderId })`) was
      // interpreted by the orders page's `<OrderDrawer syncUrl />`
      // as "open this order's drawer immediately", which made
      // multi-route publishes feel jumpy and hid the rest of the
      // batch behind a modal. The page-level "this session" list
      // still records every order so the user can re-open them
      // from there if they need to.
      if (onClose) {
        onClose();
      } else {
        navigate('dashboard');
      }
    },
  });

  const handlePublish = () => {
    if (!isReady) {
      const message = t('wizard.publish.incomplete', {
        defaultValue: 'Fill in customer, route, load and pickup date first.',
      });
      if (onValidationError) onValidationError(message);
      else toast.warning(message);
      return;
    }
    if (isGoodsValueRequired && itemCost == null) {
      const message = t('wizard.publish.goods_value_required', {
        defaultValue:
          'Add the goods value — your organisation issues government waybills, which require it.',
      });
      if (onValidationError) onValidationError(message);
      else toast.warning(message);
      setLoadOpen(true);
      return;
    }
    try {
      const payload = buildCreateMultiOrderPayload(usePublishOrderStore.getState());
      createOrder.mutate(payload);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      if (onValidationError) onValidationError(message);
      else toast.error(message);
    }
  };

  const handleDone = () => {
    reset();
    onClose?.();
  };

  // Fresh slate every time the page mounts/unmounts so the carrier doesn't
  // come back later and find stale "session" entries from yesterday.
  useEffect(() => {
    return () => {
      reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Seed the pickup window on first mount so the user lands on a
  // pre-filled "today/tomorrow at the recommended slot" instead of an
  // empty date row. Only runs while `pickupDate` is null — once the
  // user (or `setLoadFromUser`) has touched it, we never overwrite.
  // Mirrors the customer-dashboard pattern (`OrderLocation.tsx:195`).
  useEffect(() => {
    if (pickupDate) return;
    const range = getAutoDateRange();
    setPickupDate({
      from: range.from.toISOString(),
      to: range.to.toISOString(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <PublishGrid
        sidePanel={
          <div className="p-4">
            <PublishSidePanel
              isPublishing={createOrder.isPending}
              onPublish={handlePublish}
              onDone={onClose ? handleDone : undefined}
              onEditLoad={() => setLoadOpen(true)}
              onEditPickup={() => setPickupOpen(true)}
            />
          </div>
        }>
        <div className="p-4 space-y-4">
          <ClientSection />
          {/* TODO(shipping-revisit): CarrierSection is hidden for now —
              the store keeps `allocationStrategy` at its default of `'auto'`
              so the backend still receives a valid allocation. Re-enable
              once the shipping picker is reworked. */}
          {/* <CarrierSection /> */}
          <RouteSection />
        </div>
      </PublishGrid>

      <LoadDetailsFormDrawer isOpen={isLoadOpen} onClose={() => setLoadOpen(false)} />
      <PickupDateDrawer
        isOpen={isPickupOpen}
        onClose={() => setPickupOpen(false)}
        initialDateRange={
          pickupDate
            ? { from: new Date(pickupDate.from), to: new Date(pickupDate.to) }
            : null
        }
        onChange={({ dateRange }) =>
          setPickupDate({
            from: dateRange.from.toISOString(),
            to: dateRange.to.toISOString(),
          })
        }
      />
    </>
  );
}
